<script>
import { useVuelidate } from '@vuelidate/core';
import { useAccount } from 'dashboard/composables/useAccount';
import instagramClient from 'dashboard/api/channel/instagramClient';

export default {
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      errorStateMessage: '',
      errorStateDescription: '',
      isRequestingAuthorization: false,
    };
  },

  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get('code');
    const errorType = urlParams.get('error_type');
    const errorMessage = urlParams.get('error_message');

    if (errorCode || errorType || errorMessage) {
      this.hasError = true;
      if (errorType === 'InstagramAlreadyConnected') {
        this.errorStateMessage = this.$t(
          'INBOX_MGMT.ADD.INSTAGRAM.ALREADY_CONNECTED'
        );
        this.errorStateDescription = this.$t(
          'INBOX_MGMT.ADD.INSTAGRAM.ALREADY_CONNECTED_DESCRIPTION'
        );
      } else if (errorCode === '400' && errorMessage) {
        this.errorStateMessage = errorMessage;
        this.errorStateDescription = this.$t(
          'INBOX_MGMT.ADD.INSTAGRAM.ERROR_DESCRIPTION'
        );
      } else {
        // Never show the raw server message, it is technical.
        this.errorStateMessage = this.$t('INBOX_MGMT.ADD.INSTAGRAM.API_ERROR');
        this.errorStateDescription = this.$t(
          'INBOX_MGMT.ADD.INSTAGRAM.ERROR_DESCRIPTION'
        );
      }
      // Clear the params so a refresh does not bring back a stale error.
      window.history.replaceState({}, '', window.location.pathname);
    }
  },

  methods: {
    async requestAuthorization() {
      this.isRequestingAuthorization = true;
      this.hasError = false;
      try {
        const response = await instagramClient.generateAuthorization({
          accountId: this.accountId,
        });
        window.location.href = response.data.url;
      } catch (error) {
        this.hasError = true;
        this.errorStateMessage = this.$t('INBOX_MGMT.ADD.INSTAGRAM.API_ERROR');
        this.errorStateDescription = error.message;
      } finally {
        this.isRequestingAuthorization = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <div class="flex flex-col items-center justify-center h-full">
      <div
        v-if="hasError"
        data-testid="instagram_connect_error"
        class="max-w-2xl p-6 mb-8 text-red-700 bg-red-100 rounded-lg"
      >
        <h3 class="mb-2 text-lg font-semibold">
          {{ errorStateMessage }}
        </h3>
        <p class="text-sm">{{ errorStateDescription }}</p>
      </div>

      <div class="flex flex-col items-center max-w-2xl text-center">
        <div class="mb-8">
          <span class="i-ri-instagram-line text-8xl text-gradient-instagram" />
        </div>

        <h2 class="mb-4 text-3xl font-bold text-slate-800 dark:text-slate-100">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.TITLE') }}
        </h2>

        <p class="mb-8 text-lg text-slate-600 dark:text-slate-300">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.DESCRIPTION') }}
        </p>

        <div class="flex flex-col gap-4">
          <button
            data-testid="instagram_connect_button"
            class="flex items-center justify-center px-8 py-3.5 gap-2 text-white rounded-full bg-woot-500 hover:shadow-lg transition-all duration-300 min-w-[240px] overflow-hidden"
            :disabled="isRequestingAuthorization"
            @click="requestAuthorization()"
          >
            <span class="i-ri-instagram-line size-5" />
            <span class="text-base font-medium">
              {{ $t('INBOX_MGMT.ADD.INSTAGRAM.CONTINUE_WITH_INSTAGRAM') }}
            </span>
            <span v-if="isRequestingAuthorization" class="ml-2">
              <svg
                class="w-5 h-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-gradient-instagram {
  background: linear-gradient(45deg, #833ab4 0%, #fd1d1d 50%, #fcaf45 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>
